/**
 * Place names: what comes back from Photon and what the map is supposed to do with it.
 *
 * Everything here is arithmetic and string work — no network, no Leaflet — because the two
 * things that actually go wrong are both silent. Photon's `extent` is [west, north, east,
 * south], which is neither OGC bbox order nor Leaflet's corner pairs, and a transposition
 * lands the map in the sea without an error anywhere. And the zoom the map arrives at decides
 * whether the imagery layer turns on at all (MIN_IMAGERY_ZOOM): flying to a city at 12 puts
 * the picture out silently, which is a complaint we already have.
 *
 * The one thing NOT decided here is `getBoundsZoom` — it depends on the size of the viewport,
 * so faking it away from the map would be a lie. This file unwraps the box and clamps the
 * result; MapCanvas, which has an `L.Map`, does the measuring in between.
 */

import { MIN_IMAGERY_ZOOM } from "./viewport";

export interface PhotonProperties {
  name?: string;
  city?: string;
  /** Район. The part that tells five villages of the same name in one oblast apart. */
  county?: string;
  state?: string;
  country?: string;
  /** [minLon, maxLat, maxLon, minLat] — west, north, east, south. Areas only. */
  extent?: [number, number, number, number];
}

export interface PhotonFeature {
  type: "Feature";
  /** [lon, lat] — longitude first, as GeoJSON has it and as nothing else here does. */
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: PhotonProperties;
}

export interface GeoBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Where a search wants the map to go. `Point` is assignable to it: a pasted pair is one of these. */
export interface SearchTarget {
  lat: number;
  lon: number;
  /** The place's own extent, when it has one — fit this, then two steps closer. */
  box?: GeoBox;
  /** Used when there is no box: a point object has no size to fit. */
  zoom?: number;
  /** «Обнинск, Калужская область, Россия» — so the mark can be read back in words. */
  label?: string;
}

/** A village or a shop has no extent. Close enough to see the ground, above the imagery floor. */
export const POINT_PLACE_ZOOM = 15;

/** Past this the tiles are being upscaled and nothing new arrives. Source's ceiling too. */
export const MAX_PLACE_ZOOM = 18;

/** No more than two lines of context before the country: the label is one line and gets elided. */
const MAX_CONTEXT_PARTS = 2;

/**
 * One line with enough in it to tell two places of the same name apart.
 *
 * The source stopped at name/city/state/country, which separates Бзовик (Serbia) from Бзовач
 * (Ukraine) and nothing else — five Ивановки in one oblast come back as five identical lines.
 * `county` is the part that separates them, and it is skipped when it merely repeats the name
 * («Обнинск, городской округ Обнинск») — a substring test, not a list of Russian words.
 */
export const formatPlaceLabel = (props: PhotonProperties): string => {
  const name = props.name?.trim();
  const context: string[] = [];
  for (const part of [props.city, props.county, props.state]) {
    const value = part?.trim();
    if (!value) continue;
    if (name && (value === name || value.includes(name))) continue;
    if (context.includes(value)) continue;
    if (context.length >= MAX_CONTEXT_PARTS) break;
    context.push(value);
  }
  const country = props.country?.trim();
  const parts = [name, ...context, country && !context.includes(country) ? country : undefined];
  return parts.filter((part): part is string => Boolean(part)).join(", ");
};

/** Photon's [W, N, E, S] into corners that say which is which. */
export const unwrapExtent = (extent: [number, number, number, number]): GeoBox => {
  const [west, north, east, south] = extent;
  return { south, west, north, east };
};

export const placeTarget = (feature: PhotonFeature): SearchTarget => {
  const [lon, lat] = feature.geometry.coordinates;
  const label = formatPlaceLabel(feature.properties);
  const extent = feature.properties.extent;
  if (!extent) return { lat, lon, zoom: POINT_PLACE_ZOOM, label };
  return { lat, lon, box: unwrapExtent(extent), label };
};

/**
 * The floor is 14, not 12: below it the imagery layer refuses to draw, so arriving over a
 * city at an overview zoom would put the picture out without saying anything. The cost of the
 * floor is that a large city does not fit on screen — the right trade for a tool about fields.
 */
export const clampPlaceZoom = (zoom: number): number =>
  Math.min(Math.max(Math.round(zoom), MIN_IMAGERY_ZOOM), MAX_PLACE_ZOOM);
